import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Avatar,
    Tooltip,
    IconButton,
    FormControlLabel,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { containers } from '../theme/commonStyles';
import PlayerCard from './PlayerCard';
import SidelinedIcon from './SidelinedIcon';
import { POSITION_MAP } from '../constants/game';

const PAGE_SIZE = 50;

/**
 * DraftPlayersTable - Tabel met beschikbare spelers voor de draft.
 * Toont alle spelers van uitgeschakelde deelnemers + vrije spelers.
 * Bevat filtering op categorie en infinite scroll paginering.
 *
 * @param {object} props
 * @param {Array} props.players - Beschikbare spelers voor de draft
 * @param {Function} props.onAcquire - Callback bij keuze van speler
 * @param {boolean} props.isMyTurn - Of het de beurt is van de huidige gebruiker
 * @param {Set} props.draftPickClubs - Set van clubnamen die al gekozen zijn in de draft door de huidige gebruiker
 * @param {Map} props.validationMessages - Map playerId → foutmelding
 * @param {Map} props.offeringsMap - Map playerId → { profitPercentage, offeredByName }
 * @param {Map} props.participantsMap - Map uid → naam (voor eigenaar)
 * @param {Map} props.playerOwnerMap - Map playerId → ownerUid (huidige eigenaar)
 * @param {boolean} props.isMobile - Mobiel scherm
 * @param {boolean} props.isDraftActive - Of de draft actief is
 * @param {Map} props.sidelinedMap - Map speler-id (string) → blessure/schorsing-record
 */
const DraftPlayersTable = ({
    players,
    onAcquire,
    isMyTurn,
    draftPickClubs,
    validationMessages,
    offeringsMap,
    participantsMap,
    playerOwnerMap,
    isMobile,
    isDraftActive,
    sidelinedMap
}) => {
    // Filter state: alleen "Aangeboden spelers" staat standaard aan
    const [showOffered, setShowOffered] = useState(true);
    const [showFreeAgents, setShowFreeAgents] = useState(false);

    // Paginering state
    const [currentPage, setCurrentPage] = useState(1);
    const observer = useRef();

    const getDisabledReason = (player) => {
        if (!isDraftActive) return 'De draft is niet actief.';
        if (!isMyTurn) return 'Het is niet jouw beurt.';
        const clubBlocked = player.clubName && draftPickClubs.has(player.clubName);
        if (clubBlocked) return `Je hebt al een speler van ${player.clubName} gekozen in de draft.`;
        const validationError = validationMessages?.get(player.id);
        if (validationError) return validationError;
        return null;
    };

    // Filter spelers op basis van de vinkjes
    const filteredPlayers = useMemo(() => {
        if (!showOffered && !showFreeAgents) return [];

        return players.filter((player) => {
            const offering = offeringsMap?.get(String(player.id));
            if (offering) return showOffered;
            return showFreeAgents;
        });
    }, [players, offeringsMap, showOffered, showFreeAgents]);

    // Paginering: toon alleen de eerste currentPage * PAGE_SIZE spelers
    const visiblePlayers = useMemo(() => {
        return filteredPlayers.slice(0, currentPage * PAGE_SIZE);
    }, [filteredPlayers, currentPage]);

    // Reset paginering bij filter wijziging
    const handleFilterChange = (setter) => (e) => {
        setter(e.target.checked);
        setCurrentPage(1);
    };

    // IntersectionObserver voor infinite scroll
    const lastPlayerElementRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visiblePlayers.length < filteredPlayers.length) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [filteredPlayers.length, visiblePlayers.length]);

    // Tel aantallen per categorie (voor labels)
    const counts = useMemo(() => {
        let offered = 0, free = 0;
        players.forEach((player) => {
            const offering = offeringsMap?.get(String(player.id));
            if (offering) offered++;
            else free++;
        });
        return { offered, free };
    }, [players, offeringsMap]);

    const hasMorePages = visiblePlayers.length < filteredPlayers.length;

    // Filter UI
    const filterControls = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 0 : 1, mb: 2, px: 1 }}>
            <FormControlLabel
                control={<Checkbox checked={showOffered} onChange={handleFilterChange(setShowOffered)} size="small" />}
                label={<Typography variant="body2">Aangeboden spelers ({counts.offered})</Typography>}
            />
            <FormControlLabel
                control={<Checkbox checked={showFreeAgents} onChange={handleFilterChange(setShowFreeAgents)} size="small" />}
                label={<Typography variant="body2">Overige vrije spelers ({counts.free})</Typography>}
            />
        </Box>
    );

    if (players.length === 0) {
        return (
            <>
                {filterControls}
                <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                    Geen beschikbare spelers gevonden.
                </Typography>
            </>
        );
    }

    if (filteredPlayers.length === 0) {
        return (
            <>
                {filterControls}
                <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                    Geen spelers gevonden met de geselecteerde filters.
                </Typography>
            </>
        );
    }

    if (isMobile) {
        return (
            <>
                {filterControls}
                <Box sx={containers.mobileCardList}>
                    {visiblePlayers.map((player, index) => {
                        const disabledReason = getDisabledReason(player);
                        const isDisabled = !!disabledReason;
                        const offering = offeringsMap?.get(String(player.id));
                        const ownerUid = playerOwnerMap?.get(String(player.id));
                        const ownerName = ownerUid ? participantsMap?.get(ownerUid) : null;
                        const isLast = index === visiblePlayers.length - 1;

                        return (
                            <PlayerCard
                                key={player.id}
                                ref={isLast ? lastPlayerElementRef : null}
                                player={player}
                                status={sidelinedMap?.get(String(player.id)) || null}
                                subCaption={ownerName && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        Team: {ownerName}
                                    </Typography>
                                )}
                                rightSlot={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        {offering && (
                                            <Chip
                                                label={`${offering.profitPercentage}%`}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        )}
                                        <Tooltip title={disabledReason || 'Kies deze speler'}>
                                            <span>
                                                <IconButton color="primary" onClick={() => onAcquire(player)} disabled={isDisabled}>
                                                    <GetAppIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        );
                    })}
                </Box>
                {hasMorePages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </>
        );
    }

    return (
        <>
            {filterControls}
            <TableContainer component={Paper}>
                <Table aria-label="draft spelers tabel">
                    <TableHead>
                        <TableRow>
                            <TableCell>Speler</TableCell>
                            <TableCell>Positie</TableCell>
                            <TableCell>Club</TableCell>
                            <TableCell>Eigenaar</TableCell>
                            <TableCell align="center">Aangeboden</TableCell>
                            <TableCell align="right">Actie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visiblePlayers.map((player, index) => {
                            const dutchPosition = POSITION_MAP[player.position] || player.position;
                            const disabledReason = getDisabledReason(player);
                            const isDisabled = !!disabledReason;
                            const offering = offeringsMap?.get(String(player.id));
                            const ownerUid = playerOwnerMap?.get(String(player.id));
                            const ownerName = ownerUid ? participantsMap?.get(ownerUid) : null;
                            const isLast = index === visiblePlayers.length - 1;

                            return (
                                <TableRow key={player.id} hover ref={isLast ? lastPlayerElementRef : null}>
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar src={player.photoUrl} alt={player.name} />
                                            {player.name}
                                            <SidelinedIcon status={sidelinedMap?.get(String(player.id)) || null} />
                                        </Box>
                                    </TableCell>
                                    <TableCell>{dutchPosition}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {player.clubLogo && <Box component="img" src={player.clubLogo} alt={`${player.clubName} logo`} sx={{ height: 24, width: 24, objectFit: 'contain' }} />}
                                            {player.clubName}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {ownerName || <Typography variant="body2" color="text.secondary">Vrij</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        {offering ? (
                                            <Chip 
                                                label={`${offering.profitPercentage}% winst`}
                                                size="small" 
                                                color="secondary" 
                                                variant="outlined"
                                            />
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={disabledReason || ''}>
                                            <span>
                                                <Button 
                                                    variant="contained" 
                                                    color="primary" 
                                                    size="small" 
                                                    onClick={() => onAcquire(player)} 
                                                    disabled={isDisabled}
                                                >
                                                    Kies
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {hasMorePages && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </>
    );
};

export default DraftPlayersTable;
